import { format } from "date-fns";

export interface CrewCallsheetAssignmentRow {
  stream?: {
    id?: number;
    date?: string;
    start?: string;
    end?: string;
  };
}

/** Values from `/api/streams/stream/:id/time-algorithim/` (e.g. `HH:mm:ss` or `N/A`). */
export type StreamTimeAllocationSlice = {
  call_time?: string;
  depart_time?: string;
};

export interface CrewCallsheetResponse {
  results: CrewCallsheetAssignmentRow[];
}

export const streamDateYmd = (stream?: CrewCallsheetAssignmentRow["stream"]) => {
  if (!stream?.date) return null;
  return stream.date.length >= 10 ? stream.date.slice(0, 10) : stream.date;
};

export const isStreamScheduledToday = (stream?: CrewCallsheetAssignmentRow["stream"]) => {
  const today = format(new Date(), "yyyy-MM-dd");
  return streamDateYmd(stream) === today;
};

export const isStreamHappeningNow = (stream?: CrewCallsheetAssignmentRow["stream"]) => {
  if (!stream?.date || !stream.start || !stream.end) {
    return false;
  }

  const today = format(new Date(), "yyyy-MM-dd");
  const currentTime = format(new Date(), "HH:mm:ss");
  const start = stream.start.length === 5 ? `${stream.start}:00` : stream.start;
  const end = stream.end.length === 5 ? `${stream.end}:00` : stream.end;
  const streamDay = streamDateYmd(stream);

  return streamDay === today && start <= currentTime && currentTime <= end;
};

const toComparableTime = (raw: string | undefined): string | null => {
  if (!raw || raw === "N/A") return null;
  const t = raw.trim();
  if (t.length === 5) return `${t}:00`;
  if (t.length >= 8) return t.slice(0, 8);
  return null;
};

/** End of the "assigned" window on the stream day: depart_time from allocation, else stream end. */
const assignmentDayEndTime = (
  stream: CrewCallsheetAssignmentRow["stream"] | undefined,
  allocation: StreamTimeAllocationSlice | undefined,
): string | null => {
  const depart = toComparableTime(allocation?.depart_time);
  if (depart) return depart;
  if (stream?.end) {
    const e = stream.end.trim();
    return e.length === 5 ? `${e}:00` : e.length >= 8 ? e.slice(0, 8) : null;
  }
  return null;
};

/**
 * On the stream's calendar day, crew is assigned from the start of that day until depart_time
 * (call time does not delay the label — e.g. 6am shows Assigned when call is 11am and depart is 4pm).
 */
export const isAssignedOnStreamDayUntilDepartNow = (
  stream: CrewCallsheetAssignmentRow["stream"] | undefined,
  allocation: StreamTimeAllocationSlice | undefined,
): boolean => {
  if (!isStreamScheduledToday(stream)) return false;
  const end = assignmentDayEndTime(stream, allocation);
  if (!end) return false;
  const now = format(new Date(), "HH:mm:ss");
  return now <= end;
};

/**
 * On Stream → Assigned → Available.
 * When `timeAllocationByStreamId` is set, Assigned means: stream is dated today and now is not after
 * depart_time (allocation) or stream end if depart is missing; otherwise Assigned is any assignment dated today.
 */
export const resolveStatusFromCallsheetResults = (
  results: CrewCallsheetAssignmentRow[],
  timeAllocationByStreamId?: ReadonlyMap<number, StreamTimeAllocationSlice>,
): "On Stream" | "Assigned" | "Available" => {
  if (results.some((row) => isStreamHappeningNow(row.stream))) {
    return "On Stream";
  }
  if (timeAllocationByStreamId) {
    if (
      results.some((row) => {
        const id = row.stream?.id;
        if (id == null) return false;
        return isAssignedOnStreamDayUntilDepartNow(
          row.stream,
          timeAllocationByStreamId.get(id),
        );
      })
    ) {
      return "Assigned";
    }
  } else if (results.some((row) => isStreamScheduledToday(row.stream))) {
    return "Assigned";
  }
  return "Available";
};
